import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { MaintenanceReport, MAINTENANCE_PROBLEMS } from '@/models/maintenance';
import { completeMaintenanceReport } from '@/services/maintenanceService';
import { toast } from 'sonner';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Loader2 } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationContext';
import CompletedMaintenanceNotificationToast from './CompletedMaintenanceNotificationToast';

interface MaintenanceCardProps {
  report: MaintenanceReport;
  onComplete: () => void;
}

const MaintenanceCard: React.FC<MaintenanceCardProps> = ({ report, onComplete }) => {
  const [timeRemaining, setTimeRemaining] = useState<number>(100);
  const [isCompleting, setIsCompleting] = useState(false);
  const { refreshNotifications, addCompletedNotification } = useNotifications();

  // Calcular el tiempo restante y actualizar la barra de progreso
  useEffect(() => {
    const calculateTimeRemaining = () => {
      const createdAt = parseISO(report.createdAt);
      const estimatedEndTime = new Date(createdAt);
      estimatedEndTime.setHours(estimatedEndTime.getHours() + report.estimatedTimeHours);
      
      const now = new Date();
      const totalDuration = estimatedEndTime.getTime() - createdAt.getTime();
      const elapsed = now.getTime() - createdAt.getTime();
      
      // Calcular el porcentaje de tiempo transcurrido (invertido para mostrar tiempo restante)
      const percentElapsed = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
      const percentRemaining = Math.max(0, 100 - percentElapsed);
      
      return percentRemaining;
    };

    // Calcular inicialmente
    setTimeRemaining(calculateTimeRemaining());

    // Actualizar cada minuto
    const interval = setInterval(() => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);
    }, 60000);

    return () => clearInterval(interval);
  }, [report]);

  // Determinar el color de la barra de progreso según el tiempo restante
  const getProgressColor = () => {
    if (timeRemaining > 66) return 'bg-green-500';
    if (timeRemaining > 33) return 'bg-amber-500';
    return 'bg-red-500';
  };

  // Obtener los nombres de los problemas seleccionados
  const getSelectedProblems = () => {
    return report.problems.map(problemType => {
      const problem = MAINTENANCE_PROBLEMS.find(p => p.type === problemType);
      return problem ? `${problem.icon} ${problem.label}` : problemType;
    });
  };

  // Manejar la acción de completar el mantenimiento
  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      const result = await completeMaintenanceReport(report.id);
      if (result) {
        // Crear notificación de mantenimiento completado
        const completedNotification = {
          id: result.id,
          screenId: result.screenId,
          screenName: result.screen?.name || `Pantalla ${result.screenId}`,
          screenLocation: result.screen?.location,
          problems: result.problems,
          details: result.details || '',
          createdAt: result.createdAt,
          completedAt: result.completedAt || new Date().toISOString(),
          estimatedTimeHours: result.estimatedTimeHours,
          read: false,
          completed: true
        };
        
        // Mostrar notificación de completado
        toast.custom((t) => (
          <CompletedMaintenanceNotificationToast 
            notification={completedNotification} 
            onClose={() => toast.dismiss(t)}
          />
        ), {
          duration: 8000,
          position: 'top-center'
        });
        
        // Agregar a las notificaciones completadas
        addCompletedNotification(completedNotification);
        
        // Actualizar notificaciones
        refreshNotifications();
        
        toast.success('Mantenimiento marcado como completado');
        onComplete();
      } else {
        toast.error('Error al marcar como completado');
      }
    } catch (error) {
      console.error('Error al completar el mantenimiento:', error);
      toast.error('Error al marcar como completado');
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-slate-50 pb-2">
        <div className="flex justify-between items-start">
          <CardTitle className="text-xl font-medium">
            <span className="text-2xl">🖥️</span> {report.screen?.name || `Pantalla ${report.screenId}`}
          </CardTitle>
          <Badge variant={timeRemaining > 50 ? "outline" : "destructive"} className="text-base">
            {formatDistanceToNow(parseISO(report.createdAt), { addSuffix: true, locale: es })}
          </Badge>
        </div>
        <div className="text-sm text-muted-foreground">
          {report.screen?.location || 'Ubicación no especificada'}
        </div>
      </CardHeader>
      
      <CardContent className="pt-4 space-y-3">
        <div>
          <div className="text-base font-medium mb-1">Problemas reportados:</div>
          <div className="flex flex-wrap gap-1">
            {getSelectedProblems().map((problem, index) => (
              <Badge key={index} variant="secondary" className="mb-1 text-base">
                {problem}
              </Badge>
            ))}
          </div>
        </div>
        
        {report.details && (
          <div>
            <div className="text-base font-medium mb-1">Detalles:</div>
            <p className="text-base text-muted-foreground">{report.details}</p>
          </div>
        )}
        
        <div>
          <div className="text-base font-medium mb-1">
            Tiempo restante estimado ({report.estimatedTimeHours} horas):
          </div>
          <Progress value={timeRemaining} className={getProgressColor()} />
        </div>
        
        <div className="text-sm text-muted-foreground mt-2">
          Reportado por: {report.reportedBy?.profile?.full_name || report.reportedById}
        </div>
      </CardContent>
      
      <CardFooter className="bg-slate-50 pt-2">
        <Button 
          onClick={handleComplete} 
          disabled={isCompleting}
          className="w-full text-base"
        >
          {isCompleting ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Completando...
            </>
          ) : (
            <><span className="text-xl mr-1">✅</span> Marcar como completado</>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default MaintenanceCard; 